import { datadogRum } from '@datadog/browser-rum'
import { Component, ErrorInfo, Fragment, ReactNode } from 'react'
import { CustomErrorBoundary } from './CustomErrorBoundary'
import { isTranslationResilienceActive } from './translationResilienceState'

type Props = { children: ReactNode }
type State = { error: unknown; hasError: boolean; remountKey: number }

// Browser translation reparents text nodes, so React's stale refs throw NotFoundError from
// removeChild/insertBefore during commit. Indistinguishable from real bugs statically, so we detect
// by name/message. https://github.com/facebook/react/issues/11538
const isTranslateDomCrash = (error: unknown): boolean =>
  error instanceof Error && error.name === 'NotFoundError' && /removeChild|insertBefore|not a child/i.test(error.message)

// Remount budget per healthy run: a remount that re-crashes immediately falls through to the error
// page instead of looping; a healthy render resets it.
const MAX_CONSECUTIVE_RECOVERIES = 1

export class ReactErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, remountKey: 0 }
  private consecutiveRecoveries = 0

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const translateDomCrash = isTranslateDomCrash(error)
    datadogRum.addError(error, { componentStack: info.componentStack, translateDomCrash })

    // Library active → it owns this crash class; stand down our recovery (reporting above still runs).
    if (translateDomCrash && !isTranslationResilienceActive() && this.consecutiveRecoveries < MAX_CONSECUTIVE_RECOVERIES) {
      this.consecutiveRecoveries += 1
      this.setState((state) => ({ hasError: false, error: null, remountKey: state.remountKey + 1 }))
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (!this.state.hasError && this.state.remountKey !== prevState.remountKey) {
      this.consecutiveRecoveries = 0
    }
  }

  render() {
    if (this.state.hasError) return <CustomErrorBoundary errorData={{ error: this.state.error }} />
    return <Fragment key={this.state.remountKey}>{this.props.children}</Fragment>
  }
}
